import { useMemo } from 'react'
import { useLiveWatch } from '../state/useLiveWatch'
import { StatGrid } from '../components/StatGrid'
import { orderStatTypes } from '../domain/statTypes'
import { periodLabel } from '../domain/periods'
import { teamTotal } from '../domain/derive'
import { useAuth } from '../auth/AuthProvider'
import type { PeriodFormat, StatTypeId } from '../domain/types'

/**
 * The live tally, for everyone who is not scoring.
 *
 * Read-only in the strict sense: there is no write path from this screen, and
 * the database would refuse one anyway. The grid is the same component the
 * scorer taps on, so the two cannot drift apart.
 */
export function LiveView({
  gameId,
  holderName,
  holderPlayerId,
  onTakeOver,
  onLeave,
  canScore,
}: {
  gameId: string
  holderName: string | null
  holderPlayerId?: string | null
  onTakeOver?: () => void
  onLeave?: () => void
  canScore: boolean
}) {
  const watch = useLiveWatch(gameId)
  const { member } = useAuth()

  const stats = useMemo(
    () => orderStatTypes((watch.state?.enabled_stat_types ?? ['reb', 'ast', 'stl']) as StatTypeId[]),
    [watch.state?.enabled_stat_types],
  )

  const rows = useMemo(() => {
    const published = watch.state?.roster ?? []
    if (published.length > 0) return published.map((r) => ({ id: r.id, name: r.name }))
    // No published roster yet (the scorer is on an older build, or the write
    // has not landed). Fall back to whoever has actually been tapped, so the
    // screen shows something true rather than nothing at all.
    const seen = new Map<string, string>()
    for (const e of watch.events) if (!seen.has(e.playerId)) seen.set(e.playerId, e.playerId)
    return [...seen.keys()].map((id) => ({ id, name: 'Player' }))
  }, [watch.state?.roster, watch.events])

  const totals = useMemo(
    () => new Map(stats.map((id) => [id, teamTotal(watch.events, id)] as const)),
    [stats, watch.events],
  )

  const format = (watch.state?.period_format ?? 'halves') as PeriodFormat
  const holder = holderName ?? 'Someone'
  const isMe = holderPlayerId != null && holderPlayerId === member?.player_id

  return (
    <div className="live live--watch">
      <header className="live__header">
        {onLeave && (
          <button type="button" className="live__exit" onClick={onLeave} aria-label="Leave the live view">
            ×
          </button>
        )}
        <div className="live__match">
          <span className="live__vs">{watch.state?.home_away === 'away' ? '@' : 'vs'}</span>
          <span className="live__opponent">{watch.state?.opponent_name ?? 'Live game'}</span>
          <span className="live__date">
            {isMe ? 'You are scoring on another device' : `${holder} is scoring`}
          </span>
        </div>

        <div className="seg seg--period" aria-label="Current period">
          <span className="seg__btn seg__btn--on seg__btn--static">
            {periodLabel(format, watch.state?.current_period ?? 1)}
          </span>
        </div>

        <div className="live__actions">
          <span className={'livedot' + (watch.connected ? ' livedot--on' : '')} aria-hidden="true" />
          <span className="live__watchlabel">{watch.connected ? 'LIVE' : 'RECONNECTING'}</span>
          {canScore && onTakeOver && (
            <button type="button" className="btn btn--end" onClick={onTakeOver}>
              Take over
            </button>
          )}
        </div>
      </header>

      {watch.error && (
        <div className="banner banner--error">
          Could not reach the live tally: {watch.error}. Showing the last figures received.
        </div>
      )}

      {!watch.connected && !watch.error && (
        <div className="banner">
          Not connected to live updates. Checking every 20 seconds instead
          {watch.lastUpdateAt ? `; last update ${secondsAgo(watch.lastUpdateAt)} ago.` : '.'}
        </div>
      )}

      {watch.loading ? (
        <div className="boot">
          <div className="boot__spinner" />
        </div>
      ) : rows.length === 0 ? (
        <div className="live__empty">
          <p>{holder} has claimed this game but has not recorded anything yet.</p>
        </div>
      ) : (
        <StatGrid rows={rows} stats={stats} counts={watch.counts} totals={totals} />
      )}
    </div>
  )
}

function secondsAgo(at: number): string {
  const s = Math.max(0, Math.round((Date.now() - at) / 1000))
  if (s < 60) return `${s}s`
  return `${Math.round(s / 60)}m`
}
